import AppBar from "../../components/atoms/appbar";
import Button from "../../components/atoms/button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMainGolfParkDetail } from "../../@apis/hooks/useMain";
import { cn } from "../../lib/utils/cn";
import { vw } from "../../lib/utils/responsiveUtils";
import { staticURL } from "../../lib/utils/imageUtils";
import { useModalStore } from "../../lib/store/useModalStore";
import type { GolfParkSearchResponse } from "../../@apis/dto/response/main.response";
import SelectBox from "../../components/atoms/select";
import type { postScoreCardForm } from "../../@apis/dto/request/score.request";
import { postScoreCard } from "../../@apis/v1/score.service";
import * as React from "react";
import { getQueryClient } from "../../@apis/queryClient";
import { alertRequestFailure } from "../../@apis/axios.config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ko } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import { HOLES_PER_COURSE, MAX_COURSE_COUNT } from "../../lib/utils/score.utils";
registerLocale("ko", ko);

const DEFAULT_COURSE_COUNT = 2;

type ScoreCardFormState = Omit<postScoreCardForm, "courseCodes"> & {
  courseCodes: string[];
};

const courseCountOptions = Array.from({ length: MAX_COURSE_COUNT }, (_, i) => ({
  label: `${(i + 1) * HOLES_PER_COURSE}홀 (코스 ${i + 1}개)`,
  value: String(i + 1),
}));

export default function ScoreFormPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal } = useModalStore();

  const _store = searchParams.get("store");

  const { data: storeData, isSuccess: storeSuccess } = useQuery({
    ...useMainGolfParkDetail(_store || ""),
    enabled: !!_store,
  });
  const { golfPark } = storeData?.data || {};

  // STATE
  const [courseList, setCourseList] = React.useState<
    { label: string; value: string }[]
  >([]);

  const [form, setForm] = React.useState<ScoreCardFormState>({
    golfParkCode: _store ?? "",
    roundDate: "",
    courseCodes: Array.from({ length: DEFAULT_COURSE_COUNT }, () => ""),
  });

  const changeCourseCount = (count: number) =>
    setForm((prev) => ({
      ...prev,
      courseCodes: Array.from(
        { length: count },
        (_, i) => prev.courseCodes[i] ?? "",
      ),
    }));

  const changeCourseCode = (index: number, code: string) =>
    setForm((prev) => ({
      ...prev,
      courseCodes: prev.courseCodes.map((c, i) => (i === index ? code : c)),
    }));

  const CreateScoreCardMutation = useMutation({
    mutationFn: postScoreCard,
    onSuccess: (res) => {
      if (!res.success) return;
      navigate(`/score/${res.data.scoreCardList[0].scoreCardId}`, {
        replace: true,
      });
      getQueryClient().invalidateQueries({ queryKey: ["score-list"] });
    },
    onError: (err) => alertRequestFailure("스코어카드 생성 실패", err),
  });

  const submit = () => {
    if (!form.golfParkCode) {
      openModal("ALERT_DIALOG", {
        title: "골프장 선택",
        content: "골프장을 선택해 주세요.",
      });
      return;
    }
    if (!form.roundDate) {
      openModal("ALERT_DIALOG", {
        title: "라운드 날짜",
        content: "라운드 날짜를 선택해 주세요.",
      });
      return;
    }
    CreateScoreCardMutation.mutate({
      ...form,
      courseCodes: form.courseCodes.map((c) => c || null),
    });
  };

  React.useEffect(() => {
    if (!storeSuccess || !golfPark?.golfCourseList) return;

    const cList = golfPark?.golfCourseList.map((props) => ({
      label: props.name,
      value: props.code,
    }));
    setCourseList(cList);

    setForm((prev) => ({
      ...prev,
      courseCodes: prev.courseCodes.map(() => ""),
    }));
  }, [golfPark?.golfCourseList, storeSuccess]);

  const DateInput = React.forwardRef<
    HTMLButtonElement,
    { value?: string; onClick?: () => void }
  >(({ value, onClick }, ref) => {
    return (
      <button
        type="button"
        onClick={onClick}
        ref={ref}
        className="w-full font-[500] border border-[#e6e6e6] rounded-[10px] h-[50px] px-[17px] text-left text-[0.7rem]"
      >
        <span className={value ? "text-[#222]" : "text-[#ccc]"}>
          {value || "날짜를 선택해주세요."}
        </span>
      </button>
    );
  });

  return (
    <div className="relative pb-[150px]">
      <AppBar title="스코어 입력" onBack={() => navigate("/score")} />
      <form>
        <section className="px-[15px] pb-[80px]">
          <Button
            className="w-full border border-[#e6e6e6]"
            variant={"white"}
            label=""
            type="button"
            onClick={() =>
              openModal("SELECT_GOLFPARKS_MODAL", {
                onClick: (item: GolfParkSearchResponse) => {
                  setSearchParams(
                    { store: item.code },
                    { replace: true },
                  );
                  setForm((prev) =>
                    prev.golfParkCode === item.code
                      ? prev
                      : {
                          ...prev,
                          golfParkCode: item.code,
                          courseCodes: prev.courseCodes.map(() => ""),
                        },
                  );
                },
              })
            }
          >
            <div className="flex-row gap-[15px]">
              <img className="size-4" src="/icons/ic-flag.webp" />
              <p>골프장 선택</p>
            </div>
          </Button>

          {storeSuccess && (
            <div className="bg-[#f5f5f5] px-[20px] py-[14px] rounded-[15px] gap-x-[20px] mb-[15px] flex mt-[10px]">
              {golfPark?.imageUrl ? (
                <img
                  src={staticURL(golfPark.imageUrl)}
                  alt="_blank"
                  className="rounded-[10px] shrink-0"
                  style={{ width: vw(83), height: vw(83) }}
                />
              ) : (
                <img
                  src="/images/img-login-logo.webp"
                  alt="_blank"
                  className="rounded-[10px] shrink-0 mt-[6px]"
                  style={{ width: vw(83), height: vw(83) }}
                />
              )}

              <div>
                <p className="font-bold text-2xl">{golfPark?.name}</p>
                <div className="flex gap-[5px] items-start mt-[14px] font-[500] text-[#888] text-sm ">
                  <img
                    src="/icons/ic-place-gray.webp"
                    alt="place"
                    className="size-[0.9rem] mt-[4px]"
                  />
                  <p className="whitespace-pre-line break-keep break-words">
                    {golfPark?.address}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-[25px] space-y-[20px]">
            <p className="font-bold">라운드 날짜</p>
            {/* <div className="relative">
              <Input
                placeholder="날짜를 입력해주세요."
                type="date"
                className="w-full pr-[40px]" 
                style={{ fontSize: vw(14) }}
                value={form.roundDate}
                onChange={(e) => setForm((prev) => ({...prev, roundDate: e.target.value}))}
              />
              <img
                src="/icons/ic-calendar.webp"
                alt="calendar"
                className="absolute right-[20px] top-1/2 -translate-y-1/2 opacity-70 pointer-events-none"
                style={{ width: vw(16), height: vw(16) }}
              />
            </div> */}
            <div className="relative">
              <DatePicker
                locale="ko"
                selected={form.roundDate ? new Date(form.roundDate) : null}
                onChange={(date) =>
                  setForm((prev) => ({
                    ...prev,
                    roundDate: date ? format(date, "yyyy-MM-dd") : "",
                  }))
                }
                dateFormat="yyyy년 M월 d일"
                wrapperClassName="w-full"
                customInput={<DateInput />}
                withPortal
              />
              <img
                src="/icons/ic-calendar.webp"
                alt="calendar"
                className="size-4 absolute right-[20px] top-1/2 -translate-y-1/2 opacity-70 pointer-events-none"
              />
            </div>
          </div>

          <div className="mt-[25px] space-y-[20px]">
            <p className="font-bold">홀 수</p>
            <SelectBox
              className="font-[500] text-[0.7rem] text-[#222]"
              options={courseCountOptions}
              value={String(form.courseCodes.length)}
              onChange={(e) => changeCourseCount(Number(e.target.value))}
            />
          </div>

          {courseList.length > 0 && (
            <div className="mt-[25px] space-y-[20px]">
              <p className="font-bold">코스 선택</p>
              {form.courseCodes.map((code, index) => {
                const placeholder = `${index + 1}번째 코스를 선택해주세요.`;
                return (
                  <SelectBox
                    key={index}
                    className={`font-[500] text-[0.7rem] placeholder:text-[0.7rem]  ${code ? "text-[#222]" : "text-[#ccc]"}`}
                    options={[
                      { label: placeholder, value: "" },
                      ...(storeSuccess ? courseList : []),
                    ]}
                    value={code}
                    onChange={(e) => changeCourseCode(index, e.target.value)}
                  />
                );
              })}
            </div>
          )}
        </section>

        <div
          className={cn(
            "p-[15px] fixed max-w-[440px] w-full bottom-0 flex-row-between gap-[10px] z-50",
          )}
        >
          <Button
            type="button"
            className="w-full font-bold "
            variant={"red"}
            label="스코어 입력"
            disabled={CreateScoreCardMutation.isPending}
            onClick={submit}
          />
        </div>
      </form>
    </div>
  );
}
